// Game configuration constants
export const CANVAS_WIDTH = 360;
export const CANVAS_HEIGHT = 540;
export const FPS = 60;

// Player movement/combat defaults (fallback values)
export const PLAYER_SPEED = 180;
export const BULLET_SPEED = 280;

// Game states
export const GAME_STATES = {
  HOME: 'home',
  CHARACTER_SELECT: 'characters',
  PLAYING: 'game',
  PAUSED: 'paused',
  GAME_OVER: 'gameover',
  VICTORY: 'victory',
  LEADERBOARD: 'leaderboard'
};

// Player constants
export const PLAYER_START_LIVES = 5;
export const MAX_HEARTS = 5;

// Powerup constants
export const POWERUP_TYPES = {
  BEAN: 'bean',         // +500 points
  GOLD_BEAN: 'gold_bean', // instant special charge
  HEART: 'heart',       // +1 life
  SPECIAL: 'special'    // instant special charge (unused legacy)
};

export const POWERUP_SPAWN_CHANCE = 0.24; // Reduced by ~20% from 0.3
export const POWERUP_FALL_SPEED = 60;
export const BEAN_POINTS = 500; // Increased from 20

// Special ability constants
export const SPECIAL_CHARGE_MAX = 12; // kills needed to charge
export const SPECIAL_DURATION_DEFAULT = 5000; // ms
export const SPECIAL_DURATION = 5000; // ms - default duration for special abilities

// Enemy grid layout (increased from 3 to 4 rows for more difficulty)
export const ENEMY_ROWS = 4;
export const ENEMY_COLS = 6;

// Enemy type definitions with unique behaviors and sprites
export const ENEMY_TYPES = {
  BASIC: {
    id: 'basic',
    name: 'Fried Egg',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab18ae3769a41c071c858_egg_mid.png',
    color: '#ffe5b4',
    health: 1,
    speed: 1,
    fireRate: 8000,
    points: 100,
    behavior: 'horizontal_bounce'
  },
  BACON: {
    id: 'bacon',
    name: 'Bacon',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab18a531365046c7173e2_bacon_mid.png',
    color: '#d2691e',
    health: 1,
    speed: 1,
    fireRate: 8000,
    points: 100,
    behavior: 'horizontal_bounce'
  },
  BAGEL: {
    id: 'bagel',
    name: 'Bagels',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab946391da8fd6f4f444b_beagle.png',
    color: '#daa520',
    health: 2,
    speed: 0.8,
    fireRate: 7500,
    points: 150,
    behavior: 'horizontal_bounce'
  },
  EGG: {
    id: 'egg',
    name: 'Egg',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692abeb0a39e2ef3f227a537_egg.png',
    color: '#fff8dc',
    health: 3, // Updated: tanky egg per breakfast spec
    speed: 1.2,
    fireRate: 7000,
    points: 150,
    behavior: 'horizontal_bounce', // Changed from zigzag to horizontal movement
    hitboxWidth: 26, // Reduced hitbox for easier dodging
    hitboxHeight: 26
  },
  CAPPUCCINO: {
    id: 'cappuccino',
    name: 'Cappuccino',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab185662298ea56db3c81_capp.png',
    color: '#8b4513',
    health: 2, // Updated: mid-tier per breakfast spec
    speed: 1.0, // Changed from 0.5 to enable horizontal movement
    fireRate: 6000,
    points: 180,
    behavior: 'horizontal_bounce' // Changed from stationary to Space Invaders movement
  },
  COOKIES: {
    id: 'cookies',
    name: 'Cookies',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692abf2e3d052fbcb107c745_cookie.png',
    color: '#cd853f',
    health: 1, // Updated: tiny fodder per breakfast spec
    speed: 1.0, // Changed to normalize movement
    fireRate: 8000,
    points: 80,
    behavior: 'horizontal_bounce' // Changed from bomber to Space Invaders movement
  },
  MUFFIN: {
    id: 'muffin',
    name: 'Muffins',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692abeb7d31f195460f25084_muffin.png',
    color: '#a0522d',
    health: 3, // Updated: reduced from 4 per breakfast spec
    speed: 1.0, // Changed to normalize movement
    fireRate: 8000,
    points: 250,
    behavior: 'horizontal_bounce' // Changed from tank to Space Invaders movement
  },
  CROISSANT: {
    id: 'croissant',
    name: 'Croissants',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692abde4af5da766d864c00c_cur.png',
    color: '#f4a460',
    health: 2, // Updated: mid-tier per breakfast spec
    speed: 1.0, // Changed from 0.7 to normalize movement
    fireRate: 7000,
    points: 200,
    behavior: 'horizontal_bounce' // Changed from swooper to Space Invaders movement
  },
  COFFEE_POT: {
    id: 'coffee_pot',
    name: 'Coffee Pot',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692abd3b2b54d6b1e90b9a95_coffee_pot.png',
    color: '#2f4f4f',
    health: 3, // Updated: reduced from 5 per breakfast spec
    speed: 1.0, // Changed from 0 to enable horizontal movement
    fireRate: 5500,
    points: 280,
    behavior: 'horizontal_bounce' // Changed from phaser to horizontal movement
  },
  PANCAKES: {
    id: 'pancakes',
    name: 'Pancakes',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab8d07ab8a24c59257219_pancakes.png',
    color: '#f5deb3',
    health: 3, // Updated: reduced from 6 per breakfast spec
    speed: 0.3,
    fireRate: 6500,
    points: 350,
    behavior: 'splitter',
    hitboxWidth: 26, // Reduced hitbox for easier dodging
    hitboxHeight: 26
  },
  FROG: {
    id: 'frog',
    name: 'Frog',
    sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab57bf6dc5195ae3ea594_frog_bullet.png',
    color: '#7cfc00',
    health: 2,
    speed: 0.6,
    fireRate: 5000,
    points: 180,
    behavior: 'frog_pattern'
  }
};

// Updated Character data with projectiles and unique stats
export const CHARACTERS = {
  cloaks: [
    {
      id: 'c_151',
      name: 'Cloak #151',
      displayName: 'Bat',
      side: 'cloak',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e3333bf67710ef8d0_c_151.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aac503dfa7e7ea20d8a2b_bat.png',
      specialName: 'Blood Fan Barrage',
      specialDescription: 'For 5 seconds, every shot becomes a 5-way red laser spread. Huge screen coverage.',
      specialId: 'blood_fan',
      movementPerk: 'Horizontal wrap (Pac-Man style)',
      wrapHorizontal: true,
      speed: 225, // SPEEDSTER: High mobility
      fireRate: 3.8, // shots per second - Above average
      bulletSpeed: 320, // Fast projectiles
      particleColor: 'red'
    },
    {
      id: 'c_7130',
      name: 'Cloak #7130',
      displayName: 'Scythe',
      side: 'cloak',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ee1e3bdf8eb1d3d97_c_7130.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aa46b712e732b0a2c98d7_skull_small.png',
      specialName: 'One Slice',
      specialDescription: 'Screen-wide flash that clears enemy bullets and heavily damages enemies.',
      specialId: 'one_slice',
      movementPerk: 'Horizontal wrap',
      wrapHorizontal: true,
      speed: 150, // HEAVY HITTER: Slow but powerful
      fireRate: 2.2, // Low fire rate
      bulletSpeed: 340, // Very fast bullets to compensate
      particleColor: 'lightpink'
    },
    {
      id: 'c_11292',
      name: 'Cloak #11292',
      displayName: 'Archer - Eyepatch',
      side: 'cloak',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e962d34eb4472da90_c_11292.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aa64623c9c0e6b8ebf150_fire_wheel_small.png',
      specialName: 'Orbiting Shield',
      specialDescription: 'For 10 seconds, orbiting projectiles protect you by destroying enemy bullets on contact.',
      specialId: 'orbiting_shield',
      movementPerk: 'Horizontal wrap',
      wrapHorizontal: true,
      speed: 180, // BALANCED: Middle ground
      fireRate: 3.0, // Balanced
      bulletSpeed: 290, // Balanced
      particleColor: 'yellow'
    },
    {
      id: 'c_16564',
      name: 'Cloak #16564',
      displayName: 'Cheetah',
      side: 'cloak',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e0afc9c0f75ff097c_c_16564.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aabbf65109c0f168b9386_cat_fire.png',
      specialName: 'Steam Overclock',
      specialDescription: 'For 5 seconds, rapid fire stream with damaging steam trails.',
      specialId: 'steam_overclock',
      movementPerk: 'Horizontal wrap',
      wrapHorizontal: true,
      speed: 235, // SPEEDSTER: Fastest Cloak
      fireRate: 4.2, // High fire rate
      bulletSpeed: 300, // Good speed
      particleColor: 'gold'
    },
    {
      id: 'c_19431',
      name: 'Cloak #19431',
      displayName: 'Archer - Fire',
      side: 'cloak',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24eea51a7384061698b_c_19431.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aa61365109c0f168af459_fire.png',
      specialName: 'Crema Beam',
      specialDescription: 'For 5 seconds, channel a continuous red fiery piercing beam instead of normal shots.',
      specialId: 'crema_beam',
      movementPerk: 'Horizontal wrap',
      wrapHorizontal: true,
      speed: 185, // BALANCED: Slightly above average
      fireRate: 3.3, // Good fire rate
      bulletSpeed: 310, // Above average speed
      particleColor: 'teal'
    },
    {
      id: 'c_1887',
      name: 'Cloak #1887',
      displayName: 'Forest Wolf',
      side: 'cloak',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ee3a8c903c6bf3ab0_c_1887.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aa465b1591da720e35afe_purple_small.png',
      specialName: 'Lupine Frenzy',
      specialDescription: 'For 4 seconds, aggressive speed burst with +50% movement speed and +65% fire rate.',
      specialId: 'lupine_frenzy',
      movementPerk: 'Horizontal wrap',
      wrapHorizontal: true,
      speed: 155, // HEAVY HITTER: Tank-like
      fireRate: 2.5, // Lower fire rate
      bulletSpeed: 250, // Slower bullets
      particleColor: 'purple'
    }
  ],
  migos: [
    {
      id: 'n_17763',
      name: 'Migo #17763',
      displayName: 'White Shag',
      side: 'migo',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ee043e99baed366a1_n_17763.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aad39bc5e04cdf4e60d1b_3d_bit.png',
      specialName: '3D Glitch',
      specialDescription: 'Spawn 2 hologram clones for 6 seconds. All three fire in sync.',
      specialId: '3d_glitch',
      movementPerk: 'Full vertical range, no horizontal wrap',
      wrapHorizontal: false,
      fullVertical: true,
      speed: 240, // SPEEDSTER: Fastest overall
      fireRate: 4.5, // Very high fire rate
      bulletSpeed: 335, // Fastest projectiles
      particleColor: 'cyan'
    },
    {
      id: 'N_2818',
      name: 'Migo #2818',
      displayName: 'Pigtails',
      side: 'migo',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e7e99d28670266a13_N_2818.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aac9c0202b2d312b969c1_blue_fire.png',
      specialName: 'Raid Overdrive',
      specialDescription: 'For 5 seconds, increased fire rate and quick dash with i-frames on double-tap.',
      specialId: 'raid_overdrive',
      movementPerk: 'Full vertical range',
      wrapHorizontal: false,
      fullVertical: true,
      speed: 220, // SPEEDSTER: High mobility
      fireRate: 3.9, // High fire rate
      bulletSpeed: 295, // Good speed
      particleColor: 'blue'
    },
    {
      id: 'n_6672',
      name: 'Migo #6672',
      displayName: 'Ginger',
      side: 'migo',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24eafcfe74681d63ca1_n_6672.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aabc72e0811e74e9b48f5_football.png',
      specialName: 'Chain Mines',
      specialDescription: 'For 5 seconds, double movement & fire rate, plus a 1-hit shield.',
      specialId: 'chain_mines',
      movementPerk: 'Full vertical range',
      wrapHorizontal: false,
      fullVertical: true,
      speed: 190, // BALANCED: Middle ground
      fireRate: 3.2, // Balanced
      bulletSpeed: 280, // Balanced
      particleColor: 'orange'
    },
    {
      id: 'N_9770',
      name: 'Migo #9770',
      displayName: 'Croc',
      side: 'migo',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e19872724aa3ba4ad_N_9770.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aa45d61ee23211bdb7782_chain.png',
      specialName: 'Croc Shield',
      specialDescription: 'For 5 seconds, invulnerable shield with contact damage while shooting.',
      specialId: 'croc_shield',
      movementPerk: 'Full vertical range',
      wrapHorizontal: false,
      fullVertical: true,
      speed: 145, // HEAVY HITTER: Slowest overall, tank
      fireRate: 2.0, // Very low fire rate
      bulletSpeed: 350, // Compensate with fastest bullets
      particleColor: 'green'
    },
    {
      id: 'N_19130',
      name: 'Migo #19130',
      displayName: 'Snowman',
      side: 'migo',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ec00212b1b303ce07_N_19130.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aa4e8e3769a41c0708d3c_snow.png',
      specialName: 'Frost Flurry',
      specialDescription: 'For 6 seconds, fire triple-shot snowflake spread (straight, left, right).',
      specialId: 'frost_flurry',
      movementPerk: 'Full vertical range',
      wrapHorizontal: false,
      fullVertical: true,
      speed: 195, // BALANCED: Slightly above average
      fireRate: 3.4, // Above average
      bulletSpeed: 305, // Above average
      particleColor: 'white'
    },
    {
      id: 'n_2869',
      name: 'Migo #2869',
      displayName: 'Frog',
      side: 'migo',
      sprite: 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24edcb42d79f6aef702_n_2869.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab57bf6dc5195ae3ea594_frog_bullet.png',
      specialName: 'Chomp Dash',
      specialDescription: 'Short dash with invincibility and contact damage. Heal 1 heart if enemy killed.',
      specialId: 'chomp_dash',
      movementPerk: 'Full vertical range',
      wrapHorizontal: false,
      fullVertical: true,
      speed: 160, // HEAVY HITTER: Slow and methodical
      fireRate: 2.4, // Low fire rate
      bulletSpeed: 270, // Moderate speed
      particleColor: 'lime'
    }
  ]
};

// Helper function to get character by ID
export function getCharacterById(id) {
  const allChars = [...CHARACTERS.cloaks, ...CHARACTERS.migos];
  return allChars.find(char => char.id === id);
}

// Helper function to get characters by side
export function getCharactersBySide(side) {
  return side === 'cloak' ? CHARACTERS.cloaks : CHARACTERS.migos;
}

// Level configurations - Blackbird Breakfast Campaign (5 levels)
export const LEVELS = [
  {
    waves: 1,
    boss: true,
    enemySpeed: 1.0,
    enemyHealth: 1,
    name: 'Early Bird Breakfast',
    // Level 1: Only Fried Egg and Bacon (80% Egg, 20% Bacon early → 60% Egg, 40% Bacon later)
    enemyTypes: ['basic', 'bacon'], // basic = Fried Egg
    enemyWeights: [0.70, 0.30] // Balanced mix for the whole level
  },
  {
    waves: 1,
    boss: true,
    enemySpeed: 1.2,
    enemyHealth: 2,
    name: 'Cafe Classics',
    // Level 2: Cappuccino, Croissant, Egg(tanky) (50% Cap, 40% Croi, 10% Egg early → 35% Cap, 35% Croi, 30% Egg later)
    enemyTypes: ['cappuccino', 'croissant', 'egg'],
    enemyWeights: [0.40, 0.35, 0.25] // Balanced overall mix
  },
  {
    waves: 1,
    boss: true,
    enemySpeed: 1.4,
    enemyHealth: 2,
    name: 'Coffee Rush Hour',
    // Level 3: Coffee Pot (rows), Egg, Bacon, Bagel (40% Bacon, 25% Coffee Pot, 20% Egg, 15% Bagel)
    enemyTypes: ['bacon', 'coffee_pot', 'egg', 'bagel'],
    enemyWeights: [0.40, 0.25, 0.20, 0.15]
  },
  {
    waves: 1,
    boss: true,
    enemySpeed: 1.6,
    enemyHealth: 3,
    name: 'Brunch Brigade',
    // Level 4: Pancakes, Egg, Croissant, Coffee Pot (30% Pancakes, 25% Egg, 30% Croissant, 15% Coffee Pot)
    enemyTypes: ['pancakes', 'egg', 'croissant', 'coffee_pot'],
    enemyWeights: [0.30, 0.25, 0.30, 0.15]
  },
  {
    waves: 1,
    boss: true,
    enemySpeed: 2.0,
    enemyHealth: 3,
    name: 'Cafe Chaos',
    // Level 5: All breakfast enemies (20% Croi, 15% Cookie, 15% Coffee Pot, 20% Pancakes, 20% Egg, 10% Muffin)
    enemyTypes: ['croissant', 'cookies', 'coffee_pot', 'pancakes', 'egg', 'muffin'],
    enemyWeights: [0.20, 0.15, 0.15, 0.20, 0.20, 0.10]
  }
];

// Level visual styles - Blackbird Breakfast Theme
export const LEVEL_STYLES = [
  { enemy: '#ffe5b4', bgTop: '#3a2a1a', bgBottom: '#1a1410', name: 'Early Bird Breakfast', bulletColor: '#ffd700' },
  { enemy: '#daa520', bgTop: '#2a1f15', bgBottom: '#15100a', name: 'Cafe Classics', bulletColor: '#ff8c42' },
  { enemy: '#8b4513', bgTop: '#2f1f1f', bgBottom: '#150f0f', name: 'Coffee Rush Hour', bulletColor: '#7fd0ff' },
  { enemy: '#f5deb3', bgTop: '#3a2f20', bgBottom: '#1a150f', name: 'Brunch Brigade', bulletColor: '#ff6b9d' },
  { enemy: '#cd853f', bgTop: '#352015', bgBottom: '#1a100a', name: 'Cafe Chaos', bulletColor: '#b6ff7a' }
];

// Enemy constants
export const ENEMY_SPEED_BASE = 40;
export const ENEMY_HORIZONTAL_SPEED = 50; // Horizontal movement speed for Space Invaders formation
export const ENEMY_FIRE_INTERVAL_MIN = 2.0; // Minimum seconds between enemy shots
export const ENEMY_FIRE_INTERVAL_MAX = 4.0; // Maximum seconds between enemy shots
export const BOSS_HEALTH_MULTIPLIER = [12, 24, 42, 62, 82]; // HP per level

// Bean pickup sprite
export const BEAN_SPRITE = 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ab185728dea0662ee768a_bean_mid.png';
